import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { ConfigProvider, theme } from 'antd';

interface ThemeContextType {
    primaryColor: string;
    setPrimaryColor: (color: string) => void;
    themeMode: 'light' | 'dark';
    toggleThemeMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [primaryColor, setPrimaryColorState] = useState<string>('#086872');
    const [themeMode, setThemeMode] = useState<'light' | 'dark'>('light');

    useEffect(() => {
        const savedColor = localStorage.getItem('theme-primary-color');
        if (savedColor) {
            setPrimaryColorState(savedColor);
            document.documentElement.style.setProperty('--theme-color', savedColor);
        } else {
            // Initialize with default if not set
            const style = getComputedStyle(document.body);
            const defaultColor = style.getPropertyValue('--theme-color');
            if (defaultColor) {
                setPrimaryColorState(defaultColor.trim());
            }
        }

        const savedMode = localStorage.getItem('theme-mode') as 'light' | 'dark';
        if (savedMode) {
            setThemeMode(savedMode);
        }
    }, []);

    const setPrimaryColor = (color: string) => {
        setPrimaryColorState(color);
        localStorage.setItem('theme-primary-color', color);
        document.documentElement.style.setProperty('--theme-color', color);
    };

    const toggleThemeMode = () => {
        const newMode = themeMode === 'light' ? 'dark' : 'light';
        setThemeMode(newMode);
        localStorage.setItem('theme-mode', newMode);
    };

    return (
        <ThemeContext.Provider value={{ primaryColor, setPrimaryColor, themeMode, toggleThemeMode }}>
            <ConfigProvider
                theme={{
                    algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    token: {
                        colorPrimary: primaryColor,
                        colorBgLayout: '#FFFFFF',
                        colorBgContainer: '#FFFFFF',
                        colorBgElevated: '#FFFFFF',
                        fontFamily: "'TikTok Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                        borderRadius: 8,
                        colorTextBase: '#1f1f1f',
                        colorTextSecondary: '#595959',
                    },
                    components: {
                        Layout: {
                            colorBgBody: '#FFFFFF',
                            colorBgHeader: '#FFFFFF',
                        },
                        Card: {
                            colorBgContainer: '#FFFFFF',
                            boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
                        },
                        Table: {
                            headerBg: '#FAFAFA',
                            rowHoverBg: '#F5F5F5',
                        }
                    }
                }}
            >
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
